import { Button } from "@/components/ui/button";
import * as Card from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { Text } from "@/components/ui/text";
import type { CreateQueryResult } from "@tanstack/solid-query";
import { CircleAlert, Inbox, RefreshCw } from "lucide-solid";
import { type JSX, Match, Switch } from "solid-js";
import { Box } from "styled-system/jsx";
import { flex } from "styled-system/patterns";

// --- Shared Wrapper ---
const StateWrapper = (props: { children: JSX.Element }) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="center"
    width="100%"
    minHeight="250px"
    p={4}
    bg="bg.canvas"
  >
    {props.children}
  </Box>
);

// --- Loading Component ---
interface QueryLoadingProps {
  message?: string;
}

export const QueryLoading = (props: QueryLoadingProps) => (
  <StateWrapper>
    <Box class={flex({ direction: "column", align: "center", gap: 3 })}>
      <Spinner />
      {props.message && <Text color="fg.muted">{props.message}</Text>}
    </Box>
  </StateWrapper>
);

// --- Error Component ---
interface QueryErrorProps {
  error?: any;
  refetch?: () => void;
  title?: string;
  message?: string;
}

export const QueryError = (props: QueryErrorProps) => (
  <StateWrapper>
    <Card.Root>
      <Card.Header class={flex({ direction: "column", align: "center", gap: 2, textAlign: "center" })}>
        <CircleAlert />
        <Card.Title>{props.title || "Something went wrong"}</Card.Title>
        <Card.Description>
          {props.message || props.error?.message || "We couldn't load the data. Please try again."}
        </Card.Description>
      </Card.Header>
      {props.refetch && (
        <Card.Footer class={flex({ justify: "center" })}>
          <Button size="sm" variant="outline" onClick={() => props.refetch?.()}>
            <RefreshCw />
            Try Again
          </Button>
        </Card.Footer>
      )}
    </Card.Root>
  </StateWrapper>
);

// --- Empty Component ---
interface QueryEmptyProps {
  title?: string;
  message?: string;
  action?: JSX.Element;
}

export const QueryEmpty = (props: QueryEmptyProps) => (
  <StateWrapper>
    <Card.Root>
      <Card.Header class={flex({ direction: "column", align: "center", gap: 2, textAlign: "center" })}>
        <Inbox />
        <Card.Title>{props.title || "No items found"}</Card.Title>
        <Card.Description>
          {props.message || "There is no data to display right now."}
        </Card.Description>
      </Card.Header>
      {props.action && (
        <Card.Footer class={flex({ justify: "center" })}>
          {props.action}
        </Card.Footer>
      )}
    </Card.Root>
  </StateWrapper>
);

// --- Data Guard Component ---
// --- Data Guard Component ---

/** * We define the exact shape DataGuard needs.
 * This prevents TypeScript errors when TanStack changes its internal types.
 */
interface GuardedQuery<T, TError = Error> {
  isPending: boolean;
  isError: boolean;
  error: TError | null;
  data: T | undefined;
  refetch: () => void;
}

interface DataGuardProps<T, TError = Error> {
  query: GuardedQuery<T, TError>;
  /** Optional override for the loading state text */
  loadingMessage?: string;
  /** Optional override for the error state text */
  errorMessage?: string;
  /** Optional override for the empty state text */
  emptyMessage?: string;
  /** The content to show when data is ready */
  children: (data: NonNullable<T>) => JSX.Element;
}

export function DataGuard<T, TError = Error>(props: DataGuardProps<T, TError>) {
  return (
    <Switch>
      <Match when={props.query.isPending}>
        <QueryLoading message={props.loadingMessage} />
      </Match>

      <Match when={props.query.isError}>
        <QueryError
          error={props.query.error}
          refetch={props.query.refetch}
          message={props.errorMessage}
        />
      </Match>

      <Match
        when={
          props.query.data === undefined ||
          props.query.data === null ||
          (Array.isArray(props.query.data) && props.query.data.length === 0)
        }
      >
        <QueryEmpty message={props.emptyMessage} />
      </Match>

      <Match when={props.query.data}>
        {props.children(props.query.data as NonNullable<T>)}
      </Match>
    </Switch>
  );
}
